import {
  AfterViewInit,
  Component,
  effect,
  EventEmitter,
  input,
  Output,
  ViewChild,
} from '@angular/core';
import { DatePipe, DecimalPipe } from '@angular/common';
import {
  MatCell,
  MatCellDef,
  MatColumnDef,
  MatHeaderCell,
  MatHeaderCellDef,
  MatHeaderRow,
  MatHeaderRowDef,
  MatRow,
  MatRowDef,
  MatTable,
  MatTableDataSource,
} from '@angular/material/table';
import { MatIcon } from '@angular/material/icon';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort, MatSortHeader } from '@angular/material/sort';
import { MatTooltip } from '@angular/material/tooltip';
import { WeatherBaseline } from '../../../../../shared/classes/weather/weather-baseline';
import { MatButton } from '@angular/material/button';
import { FileDownload } from '../../../../../shared/classes/fileDownload';
import {VulcanIconComponent} from '@eagle-i/vulcan-component-library';

@Component({
  selector: 'tgrid-weather-event-table',
  imports: [
    DatePipe,
    DecimalPipe,
    MatCell,
    MatCellDef,
    MatColumnDef,
    MatHeaderCell,
    MatHeaderRow,
    MatHeaderRowDef,
    MatIcon,
    MatPaginator,
    MatRow,
    MatRowDef,
    MatSort,
    MatSortHeader,
    MatTable,
    MatHeaderCellDef,
    MatTooltip,
    MatButton,
    VulcanIconComponent,
  ],
  templateUrl: './weather-event-table.component.html',
  standalone: true,
  styleUrl: './weather-event-table.component.scss',
})
export class WeatherEventTableComponent implements AfterViewInit {
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  public events = input<WeatherBaseline[]>([]);
  @Output() selected = new EventEmitter<WeatherBaseline>;

  public readonly columns: string[] = ['state', 'county', 'phenom', 'start_date', 'end_date', 'extreme', 'auc', 'tot'];
  public dataSource: MatTableDataSource<WeatherBaseline> = new MatTableDataSource<WeatherBaseline>([]);

  constructor() {
    effect(() => {
      this.dataSource.data = this.events().map(e => new WeatherBaseline(e));
    });
  }


  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sortingDataAccessor = this.sortingDataAccessor.bind(this);
    this.dataSource.sort = this.sort;
  }

  public onExportClick() {
    const csvRowList: string[] = [];
    const header = ['State', 'County', 'Weather Events', 'Start Date', 'End Date', 'Extreme Event', 'AUC', 'TOT'];
    csvRowList.push(header.join(','));

    this.dataSource.data.forEach(row => {
      const rowData = [
        FileDownload.formatCsvCell(row.stateAbbreviation),
        FileDownload.formatCsvCell(row.county),
        FileDownload.formatCsvCell(row.weatherEventNames),
        FileDownload.formatCsvCell(row.groupIssued.format('MM/dd/yyyy') ?? ''),
        FileDownload.formatCsvCell(row.groupExpired.format('MM/dd/yyyy') ?? ''),
        FileDownload.formatCsvCell(row.extreme ? 'true' : 'false'),
        FileDownload.formatCsvCell(row.auc),
        FileDownload.formatCsvCell(row.tot),
      ];
      csvRowList.push(rowData.join(','));
    });

    const fileName = 'weather_events_report.csv';

    FileDownload.downloadCSV(fileName, csvRowList.join('\n'));
  }

  /**
   * A utility function that is used to determine if an element is showing an ellipsis
   * @param element The element being checked for ellipsis
   * @protected
   */
  // TODO: Move this to a utility function file
  protected hasEllipsis(element: HTMLTableCellElement) {
    return (window as {elementHasEllipsis?: (el: HTMLTableCellElement) => boolean}).elementHasEllipsis?.(element);
  }

  private sortingDataAccessor(row: WeatherBaseline, headerName: string) {
    switch (headerName) {
      case 'state':
        return row.stateAbbreviation;
      case 'county':
        return row.county.toLowerCase();
      case 'phenom':
        return row.weatherEventNames;
      case 'start_date':
        return row.groupIssued?.valueOf();
      case 'end_date':
        return row.groupExpired?.valueOf();
      case 'extreme':
        return row.extreme ? 1 : -1;
      case 'auc':
        return row.auc;
      case 'tot':
        return row.tot;
      default:
        return '';
    }
  }
}
